<template>
    <div
        ref="ringBuilderElement"
        class="ring-builder"
    >
        <div
            v-show="isVisible"
            class="wrapper"
        >
            <div class="rb-content">
                <div class="teleport-rb-filter">
                    <FilterButton v-if="isFilterButtonVisible" />
                </div>

                <button
                    class="items-trigger"
                    @click="toggleMobile"
                >
                    <img
                        :src="rbIcon"
                        class="rb-icon"
                        alt="Ring builder icon"
                    />

                    Your ring
                </button>

                <div
                    :class="classItems"
                    class="items"
                >
                    <div
                        v-for="(item, index) in items"
                        :key="getItemKey(item)"
                        ref="itemRefs"
                        :data-index="index"
                        class="item-wrapper"
                    >
                        <component
                            :is="getItemComponent(item)"
                            v-qa="`ring-builder-item-${getItemQaKey(item)}`"
                            :data-qa-type="getItemQaType(item)"
                            :data="item"
                            :index="index"
                            :is-final="hasAtLeastOneItem || index === 0"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import { useRoute } from 'vue-router'
import { onClickOutside, useEventListener } from '@vueuse/core'

import { assertNever } from '@/helpers'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type {
    RingBuilderRingTypes,
    RingBuilderStoneTypes,
} from '@/domain/product/ring/ring-builder'
import { useRoutes } from '@/router/routes'
import { GemsApi_StoneCategory, type StoneShapeCode } from '@/graphql/api'
import { getDocument } from '@/helpers/globals'
import {
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_ENGAGEMENT_RINGS_HOME,
} from '@/router'
import {
    useIsRingBuilderActive,
    useRingBuilder,
} from '@/plugins/ring-builder.ts'

import rbIcon from '@/assets/icons/rb.svg'

import ItemPlaceholderWithLink from './item/ItemPlaceholderWithLink.vue'
import ItemEngagementRingWithLink from './item/ItemEngagementRingWithLink.vue'
import ItemRingBuilderStoneWithLink from './item/ItemRingBuilderStoneWithLink.vue'
import FilterButton from '@/components/product/filter-button/FilterButton.vue'

export type PlaceholderRing = {
    __typename: 'placeholder-ring'
    route?: ButtonRouteRelative
}
export type PlaceholderStone = {
    __typename: 'placeholder-stone'
    route?: ButtonRouteRelative
}
export type PlaceholderPersonalization = {
    __typename: 'placeholder-personalization'
    route?: ButtonRouteRelative
}
export type RingBuilderRingWithUrl = {
    __typename: 'ring-with-url'
    ring: RingBuilderRingTypes
    route: ButtonRouteRelative
}

type RingBuilderItem =
    | RingBuilderRingWithUrl
    | RingBuilderStoneTypes
    | PlaceholderRing
    | PlaceholderStone

type RingAndStone = [RingBuilderRingWithUrl, RingBuilderStoneTypes]
type RingAndPlaceholder = [RingBuilderRingWithUrl, PlaceholderStone]
type StoneAndRing = [RingBuilderStoneTypes, RingBuilderRingWithUrl]
type StoneAndPlaceholder = [RingBuilderStoneTypes, PlaceholderRing]
type Placeholders = [PlaceholderRing, PlaceholderStone]
type RingBuilderItems =
    | RingAndStone
    | RingAndPlaceholder
    | StoneAndRing
    | StoneAndPlaceholder
    | Placeholders

const getShapeSlug = (
    stone: RingBuilderStoneTypes
): StoneShapeCode | undefined => {
    switch (stone.__typename) {
        case 'GemsApi_Stone':
            return stone.shapeWithInfo.info?.systemCode
        case 'NonSkuLabGrown':
        case 'NonSkuGemstone':
            return stone.shape.systemCode
        default:
            return assertNever(stone, undefined)
    }
}

const { ring, stone, lastAdded } = useRingBuilder()
const isRingBuilderActive = useIsRingBuilderActive()

const isVisible = computed(() => isRingBuilderActive.value)

const isMobileOpen = ref(false)
const toggleMobile = () => {
    isMobileOpen.value = !isMobileOpen.value
}
const closeMobile = () => {
    isMobileOpen.value = false
}

const ringBuilderElement = ref<HTMLElement | null>(null)

onClickOutside(ringBuilderElement, closeMobile)

const document = getDocument()

if (document) {
    useEventListener(document, 'scroll', closeMobile, { passive: true })
}

const classItems = computed(() => ({
    'items-open': isMobileOpen.value,
}))

const hasAtLeastOneItem = computed(() => Boolean(ring.value || stone.value))

const routes = useRoutes()
const route = useRoute()
const routeName = toRef(() => route.name)

const createRingWithUrl = (
    ring: RingBuilderRingTypes
): RingBuilderRingWithUrl => ({
    __typename: 'ring-with-url',
    ring,
    route: routes.ring.engagement(ring),
})

const getStonePlaceholderRoute = (): ButtonRouteRelative | undefined => {
    if (ring.value && routeName.value === ROUTE_ENGAGEMENT_RING) {
        return {
            ...routes.ring.engagement(ring.value),
            hash: '#stone-options',
        }
    }

    if (ring.value) {
        return {
            ...routes.ring.engagement(ring.value),
            hash: '#stone-options',
        }
    }

    return routes.engagementRings({})
}

// representation data
// sorts items by last added
// adds placeholders for empty slots
const items = computed((): RingBuilderItems => {
    if (ring.value && stone.value) {
        if (ring.value.__typename === lastAdded.value) {
            return [stone.value, createRingWithUrl(ring.value)]
        }

        return [createRingWithUrl(ring.value), stone.value]
    }

    if (ring.value) {
        return [
            createRingWithUrl(ring.value),
            {
                __typename: 'placeholder-stone',
                route: getStonePlaceholderRoute(),
            },
        ]
    }

    if (stone.value) {
        const shapesSlug = getShapeSlug(stone.value)

        return [
            stone.value,
            {
                __typename: 'placeholder-ring',
                route: shapesSlug
                    ? routes.engagementRings({
                          shape: shapesSlug,
                      })
                    : undefined,
            },
        ]
    }

    return [
        {
            __typename: 'placeholder-ring',
            route: routes.engagementRings({}),
        },
        {
            __typename: 'placeholder-stone',
            route: routes.diamondSearch(GemsApi_StoneCategory.Diamond),
        },
    ]
})

const getItemComponent = (item: RingBuilderItem): any => {
    switch (item.__typename) {
        case 'placeholder-ring':
            return ItemPlaceholderWithLink
        case 'placeholder-stone':
            return ItemPlaceholderWithLink
        case 'ring-with-url':
            return ItemEngagementRingWithLink
        case 'GemsApi_Stone':
            return ItemRingBuilderStoneWithLink
        case 'NonSkuGemstone':
            return ItemRingBuilderStoneWithLink
        case 'NonSkuLabGrown':
            return ItemRingBuilderStoneWithLink
        default:
            return assertNever(item, ItemPlaceholderWithLink)
    }
}

const getItemQaKey = (item: RingBuilderItem): string => {
    switch (item.__typename) {
        case 'placeholder-ring':
            return 'ring'
        case 'placeholder-stone':
            return 'stone'
        case 'ring-with-url':
            return 'ring'
        case 'GemsApi_Stone':
            return 'stone'
        case 'NonSkuGemstone':
            return 'stone'
        case 'NonSkuLabGrown':
            return 'stone'
        default:
            return assertNever(item, '')
    }
}

const getItemQaType = (item: RingBuilderItem): string => {
    switch (item.__typename) {
        case 'placeholder-ring':
            return ''
        case 'placeholder-stone':
            return ''
        case 'ring-with-url':
            return 'engagementRing'
        case 'GemsApi_Stone':
            return 'gemsApi'
        case 'NonSkuGemstone':
            return 'nonSkuGemstone'
        case 'NonSkuLabGrown':
            return 'nonSkuLabGrown'
        default:
            return assertNever(item, '')
    }
}

const getItemKey = (item: RingBuilderItem) => {
    switch (item.__typename) {
        case 'placeholder-ring':
            return 'placeholder-ring'
        case 'placeholder-stone':
            return 'placeholder-stone'
        case 'ring-with-url':
            return `CatalogueEngagementRing-${item.ring.catalogueCode}-${String(
                item.route.name
            )}`
        case 'GemsApi_Stone':
            return `GemsApi_Stone-${item.identifier}`
        case 'NonSkuGemstone':
            return `NonSkuGemstone-${item.id}`
        case 'NonSkuLabGrown':
            return `NonSkuLabGrown-${item.id}`
        default:
            return assertNever(item, 'placeholder-ring')
    }
}

const isFilterButtonVisible = computed(
    () =>
        routeName.value === ROUTE_ENGAGEMENT_RINGS ||
        routeName.value === ROUTE_ENGAGEMENT_RINGS_HOME
)
</script>

<style lang="scss" scoped>
.ring-builder {
    position: sticky;
    top: 0;
    width: 100%;
    pointer-events: none;
    z-index: 1;

    .wrapper {
        position: relative;
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: $stone-2;
        border-top: 1px solid $grey-200;

        @include min-width(1024) {
            height: 56px;
            padding: 0 20px;
        }

        @include min-width(1280) {
            padding: 0 40px;
        }

        .rb-content {
            position: relative;
            width: 100%;
            height: 100%;
            max-width: 1360px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            pointer-events: all;

            @include min-width(1024) {
                padding-bottom: 0;
                flex-direction: row-reverse;
                flex-wrap: nowrap;
            }

            .teleport-rb-filter,
            .items-trigger,
            ::v-deep(.filter-button) {
                font-family: $effra-font-stack;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-size: 0.875rem;
                user-select: none;
            }

            .items-trigger {
                display: flex;
                height: 100%;
                align-items: center;
                justify-content: center;
                order: 1;
                flex: 1 1 auto;
                cursor: pointer;

                .rb-icon {
                    vertical-align: top;
                    margin-right: 8px;
                }

                @include min-width(1024) {
                    display: none;
                }
            }

            .teleport-rb-filter:has(.filter-button) {
                display: none;
                margin-right: 16px;
                height: 100%;
                flex: 0 0 50%;
                align-items: center;
                justify-content: center;
                order: 3;

                @include min-width(1024) {
                    display: flex;
                    flex: 0 1 auto;
                }
            }

            ::v-deep(.filter-button-wrapper) {
                width: 100%;
                height: 100%;

                .filter-button {
                    margin-right: 0;
                    height: 100%;
                    width: 100%;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    @include min-width(1024) {
                        justify-content: flex-end;
                        border-left: none;
                    }
                }
            }

            .items {
                position: relative;
                margin: auto;
                height: 0;
                display: flex;
                flex: 0 0 100%;
                flex-direction: column;
                align-items: flex-start;
                order: 1;
                overflow: hidden;
                background-color: $stone-2;

                transition: height 0.15s ease;

                &.items-open {
                    height: 148px;
                    border-top: 1px solid $grey-200;
                }

                --item-width: 288px;
                --item-height: 36px;

                padding: 0 10px;

                @include min-width(360) {
                    padding: 0 20px;
                }

                @include min-width(1024) {
                    margin: auto;
                    height: 55px;
                    flex-direction: row;
                    align-items: center;
                    padding: 0;
                    flex-wrap: nowrap;
                    flex: 1 1 auto;

                    &.items-open {
                        // override in case of mobile class is applied
                        height: 55px;
                        border-top: none;
                    }

                    --item-height: unset;
                }

                :deep(.component-image.image),
                :deep(.icon) {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 8px;
                    background: $white;

                    @include min-width(1140) {
                        margin-right: 20px;
                    }
                }

                .item-wrapper {
                    margin-top: 10px;
                    width: 100%;
                    height: var(--item-height);

                    &.personalize {
                        z-index: 1;
                    }

                    @include min-width(1024) {
                        margin-top: 0;
                        padding-right: 8px;
                        max-width: 30%;
                    }

                    @include min-width(1140) {
                        padding-right: 32px;
                    }
                }
            }
        }
    }
}
</style>
